// apps/web/src/components/ui/Countdown.tsx
//
// Live countdown component. Used for:
//   - Retry-After headers (429 responses)
//   - OTP resend cooldown (45s)
//
// Disables the trigger action while the countdown is active. Renders
// the remaining time as `m:ss`. Per phase-1-5 §9.2, the retry
// control stays disabled until the timer reaches zero.

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

export interface CountdownProps {
  /** Total seconds to count down. */
  seconds: number;
  /** Triggered when the countdown hits zero. The parent typically
   *  re-enables a button or hides the countdown. */
  onDone?: () => void;
  children?: ReactNode | ((s: number) => ReactNode);
}
function format(remaining: number): string {
  const safe = Math.max(0, Math.ceil(remaining));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function Countdown({ seconds, onDone, children }: CountdownProps): JSX.Element {
  const [remaining, setRemaining] = useState(Math.max(0, seconds));
  const [done, setDone] = useState(remaining === 0);

  useEffect(() => {
    if (done) return;
    const start = Date.now();
    const target = start + remaining * 1000;
    const id = setInterval(() => {
      const left = (target - Date.now()) / 1000;
      if (left <= 0) {
        setRemaining(0);
        setDone(true);
        onDone?.();
        clearInterval(id);
      } else {
        setRemaining(left);
      }
    }, 250);
    return () => clearInterval(id);
    // `onDone` is intentionally not in deps — the parent should be
    // stable (use useCallback or memoize) but a stale closure here
    // would be harmless (it just fires the "done" event).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  if (typeof children === 'function') {
    return <>{children(remaining)}</>;
  }
  return (
    <span aria-live="polite" aria-atomic="true" className="tabular-nums">
      {children ?? format(remaining)}
    </span>
  );
}
